// Per-browser switches for practice-app features that ship dark on dev.
// Every switch is OFF by default and is turned on only in the browser that asks for it, except the ones in
// DEFAULT_ON (ON unless this browser turned them off):
//   ?x=cohorts        turn the cohort screens in Antonin's design on (remembered in this browser)
//   ?x=-cohorts       turn them off again (also remembered)
//   ?x=cohort-extras  the cohort screens that are NOT in that design (filters, programme
//                     timeline, rates, membership list). DEFAULT ON since 23 Oct (Andrew, PROD-3206): shown
//                     wherever VITE_COHORT_MEMBERSHIPS is built (dev); a prod build omits the flag, so it
//                     stays off there. Independent of ?x=cohorts.
//   ?x=-cohort-extras temporary off switch (remembered)
//   ?x=enrolment-channel  turn on the care model's enrolment channel (who enrols: reception or the
//                     physician in the care plan): the studio control and the enrol screens obeying it.
//                     Needs VITE_COHORT_MEMBERSHIPS and this switch. Ranked testing only.
//   ?x=-enrolment-channel turn it off again
//   ?x=cohort-page    the care model's "Enrolled patients" row action opens its own page
//                     (/studio/care-models/:definitionId/enrolments) instead of the list under the
//                     library table (PROD-3206, Andrew 19 Oct). DEFAULT ON since 23 Oct (Andrew): the normal
//                     behaviour wherever VITE_COHORT_PAGE is built (dev); a prod build omits the flag, so it
//                     stays off there.
//   ?x=-cohort-page   temporary off switch (remembered): back to the list under the table
//   ?x=calendar-practice-slots   Calendar practitioner cards count and offer slots for the VIEWED
//                     practice with one profile rule (practice-slot-profile.ts), and list every
//                     working window of the day. DEFAULT ON since 19 Oct (Andrew): the normal behaviour.
//   ?x=-calendar-practice-slots  temporary off switch (remembered), kept until after the exhibition
//                     (24-26 Oct); then the switch is removed.
//   ?x=intake-desk    the Document Intake lab desk on Order Requests: "Upload lab report" goes through intake
//                     (upload, read, review, Confirm linked to the order), plus the saved report and Amend.
//                     DEFAULT ON since 23 Oct (Andrew): the normal upload wherever VITE_DOCUMENT_INTAKE is built
//                     (dev); a prod build omits the flag, so it stays off there. Needs Clinic services Document Intake.
//   ?x=-intake-desk   temporary off switch (remembered): back to the plain document upload.
//   ?x=referrals      the front-desk Referrals list in the Calendar (a tab next to Order Requests): referrals
//                     the physicians sent, with a Book action that opens the create-appointment panel
//                     (PROD-1395). Needs VITE_REFERRALS and this switch. Ranked testing only.
//   ?x=-referrals     back to today's Calendar tabs
//   ?x=previsit-questionnaire   offer "Send pre-visit questionnaire" in the Automation Studio's Step
//                     choices (Workflows #187). Needs VITE_AUTOMATION_REPLY_NODES and this switch. Ranked testing only.
//   ?x=-previsit-questionnaire  turn it off again
//   ?x=care-plans     the patient chart's Care models section lists the care models the patient is on
//                     (Patient GET api/Patients/{id}/care-plans), above the Enrol action. Ranked testing only.
//   ?x=-care-plans    back to the Enrol action alone
//   ?x=pvs-visit-notes the Post Visit Summary dialog shows the signed template answers ("Visit notes"),
//                     as the printed PDF already does. Read-only. Ranked testing only.
//   ?x=-pvs-visit-notes turn it off again
//   ?x=inbox-demo     the WhatsApp inbox (Dashboard) shows the bundled demo seed instead of the live
//                     Notifications inbox API, with page-local replies (nothing is sent). PROD-1849.
//   ?x=-inbox-demo    back to the live inbox (the default)
//   ?x=inbox-media    the live WhatsApp inbox shows a patient's files, photos and voice messages in the thread
//                     (thumbnail, file chip or player, Open / Download, Send to intake for a matched patient).
//                     PROD-3335 / PROD-3340. DEFAULT ON since 25 Oct (Andrew): wherever the inbox's
//                     VITE_SCHEDULING_AI build flag is on, every browser shows them (it was per-browser, so other
//                     machines showed "[audio]").
//   ?x=-inbox-media   temporary off switch (remembered): back to the message text only
//   ?x=inbox-templates the live WhatsApp inbox's closed composer (24-hour window over) offers approved templates,
//                     filled by the server, with a preview and Send template: those of the conversation's next upcoming
//                     appointment (confirm, nudge, reminder); with none, the general follow-ups (patient's first name,
//                     clinic name). Nothing when an appointment could not be read. PROD-3341. DEFAULT ON since 25 Oct
//                     (Andrew): wherever the inbox's VITE_SCHEDULING_AI build flag is on, every browser offers them.
//   ?x=-inbox-templates temporary off switch (remembered): back to "not available yet" in the closed composer
//   ?x=clinic-values  each measured care-plan goal (chart) and Goal step (studio) shows, read-only, the
//                     clinic's standard target, the clinic's optimal range and the patient's own target,
//                     each with its source (Samer, 23 Oct). Ranked testing only.
//   ?x=-clinic-values turn it off again
//   ?x=goals          the care model studio shows the goal card at the end of the canvas: the outcome goal,
//                     its sub-goals and the track each one is served by (round-3 design, Samer 23 Oct).
//                     Ranked testing only.
//   ?x=-goals         turn it off again
//   ?x=demog-box      Document Intake review: the patient match reads as one Demographics box with every fact of
//                     the page header beside what the document's checks found (Samer, 21 Oct). Ranked testing only.
//   ?x=-demog-box     back to the Patient match block
//   ?x=care-reports   the Reports page lists the care reports (care-model enrolment, goal attainment,
//                     biomarker trends, programme adherence; Patient GET api/Reports/*). DEFAULT ON since 24 Oct
//                     (Andrew, conference demo): shown wherever VITE_CARE_REPORTS is built (dev); a prod build omits
//                     the flag, so it stays off there. Ranked testing only.
//   ?x=-care-reports  turn them off (remembered)
//   ?x=calc-autobind  the care model studio's Calculation step binds an input by itself when exactly one form
//                     answer before it matches, and offers "From the chart: latest <lab>" with a required
//                     age limit (Samer, 23 Oct). Ranked testing only.
//   ?x=-calc-autobind turn it off again
//   ?x=intake-type    Document Intake inbox: every row gets a type picker; the classifier's suggestion is pre-selected
//                     and marked "Suggested"; Confirm type saves the pick (and reads a lab report's values) (PROD-3339).
//                     DEFAULT ON since 25 Oct (Andrew): shown wherever the intake inbox shows (VITE_DOCUMENT_INTAKE
//                     and ?x=intake); no ?x=intake-type needed. Ranked testing in SHOWCASE-CHANGELOG.md.
//   ?x=-intake-type   turn it off in this browser (remembered); ?x=intake-type turns it on again
//   ?x=copay-warning  the appointment's payment section and Billing tab say when a price has no co-payment
//                     because the patient has no active insurance for the appointment's insurer (PROD-3347,
//                     Andrew 26 Oct). The price still shows; booking is not blocked. Ranked testing only.
//   ?x=-copay-warning turn it off again
//   ?x=growth-chart   the appointment's Vitals tab shows the growth chart under the vitals grid (Flutter
//                     GrowthChartGraph): WHO 0-2 / CDC 2-20 percentiles, cm/in, print. Patients aged 19 or under
//                     with a recorded sex. Read-only. Ranked testing only.
//   ?x=-growth-chart  turn it off again
//   ?x=staff-chat     internal staff chat (SR-14, Antonin's design 1 Nov): the Team dock at the bottom right of
//                     every page (collapsed bar with the unread total, open with conversations and the thread) and
//                     the Team card on the Dashboard (Notifications staff-chat API and NotificationsHub events).
//                     ON by default on dev hosts (Andrew, 3 Nov); off elsewhere. Ranked testing only.
//   ?x=-staff-chat    turn it off again
//   ?x=staff-chat-patient  the staff chat's patient threads show their patient (the row's "Patient: name" and the
//                     patient card with name, MRN and Open chart). Not decided (Antonin asked Samer: patient-data
//                     access and audit), so off by default. Needs ?x=staff-chat.
//   ?x=-staff-chat-patient turn it off again
//   ?x=step-role      the care model studio's Checklist step shows a Role picker (Physician / Nurse / Allied
//                     health); its tasks go to that role's queue. Ranked testing only.
//   ?x=-step-role     turn it off again (a role already saved is still sent)
// The choice stays after the parameter is gone from the address. Unknown names are ignored.
// Dev hosts (Andrew, 3 Nov: every change ON on dev): on dev-practice-v2 and a local build, every switch in
// DEV_DEFAULT_ON reads ON until this browser turns it off with `?x=-name` (remembered; `?x=name` turns it on
// again). Every other host keeps the defaults above, so a prod deploy does not change by itself. `inbox-demo`
// is not in the list: it swaps the live inbox for a demo seed.
// Storage can be missing or blocked (private window, blocked site data), so every read and write
// is guarded and a failure reads as the switch's default (OFF, or ON for DEFAULT_ON).
// This mirrors the `?x=` behaviour of encounter-flags in scrips-practitioner-react.

/**
 * `cohorts` - the cohort membership screens (PROD-3206), read through `useCohortMembershipsEnabled`.
 * `cohort-extras` - the screens not in the cohort design, read through `useCohortExtrasEnabled`. Default ON.
 * `enrolment-channel` - the care model's enrolment channel, read through `useEnrolmentChannelEnabled`.
 * `cohort-page` - the care model's enrolled patients on their own page, read through `useCohortPageEnabled`. Default ON.
 * `calendar-practice-slots` - the Calendar slot fix, read in `scheduling-page.tsx`.
 * `intake` - Document Intake in the patient chart (Documents inbox, upload, review), PROD-3204, read through `isIntakeOn` (needs VITE_DOCUMENT_INTAKE).
 * `intake-desk` - the Document Intake lab desk (reports queue, saved report, Amend), PROD-3204, read through `isIntakeDeskOn` (needs VITE_DOCUMENT_INTAKE). Default ON.
 * `referrals` - the front-desk Referrals tab in the Calendar, read through `useReferralsEnabled`.
 * `previsit-questionnaire` - the Automation Studio's Send pre-visit questionnaire step, read through `usePrevisitQuestionnaireEnabled`.
 * `care-plans` - the chart's list of the patient's current care models, read through `useCarePlansEnabled`.
 * `pvs-visit-notes` - the Post Visit Summary dialog's "Visit notes" (signed template answers), read in `post-visit-summary-dialog.tsx`.
 * `inbox-demo` - the WhatsApp inbox on the demo seed instead of the live API, read in `whatsapp-inbox-page.tsx`.
 * `inbox-media` - the live WhatsApp inbox's patient files and photos, read in `whatsapp-inbox-view.tsx`.
 * `clinic-values` - the read-only clinic and patient values of a goal, read through `useClinicValuesEnabled`.
 * `goals` - the studio's goal card with sub-goals and track links, read through `useGoalsEnabled`.
 * `demog-box` - the Document Intake review's Demographics box, read in `intake-review-page.tsx`.
 * `care-reports` - the care reports on the Reports page, read through `useCareReportsEnabled`. Default ON.
 * `calc-autobind` - Calculation step auto-bind and chart-lab inputs, read through `useCalcAutobindEnabled`.
 * `intake-type` - the Document Intake inbox's type picker on every row (PROD-3339), read through `isIntakeTypePickerOn`. Default ON.
 * `copay-warning` - the no-active-insurance warning on the price panels (PROD-3347), read in `co-payment-warning-banner.tsx`.
 * `growth-chart` - the growth chart on the appointment's Vitals tab, read through `useGrowthChartEnabled`.
 * `staff-chat` - internal staff chat (SR-14), read through `useStaffChatEnabled`.
 * `staff-chat-patient` - the staff chat's patient-thread parts, read through `useStaffChatPatientEnabled`.
 * `step-role` - the Checklist step's Role picker in the care model studio, read through `useStepRoleEnabled`.
 */
export type PracticeSwitch =
  | 'cohorts'
  | 'cohort-extras'
  | 'enrolment-channel'
  | 'cohort-page'
  | 'calendar-practice-slots'
  | 'intake'
  | 'intake-desk'
  | 'referrals'
  | 'previsit-questionnaire'
  | 'care-plans'
  | 'pvs-visit-notes'
  | 'inbox-demo'
  | 'inbox-media'
  | 'inbox-templates'
  | 'clinic-values'
  | 'goals'
  | 'demog-box'
  | 'care-reports'
  | 'calc-autobind'
  | 'intake-type'
  | 'copay-warning'
  | 'growth-chart'
  | 'staff-chat'
  | 'staff-chat-patient'
  | 'step-role';

const KNOWN: readonly PracticeSwitch[] = [
  'cohorts',
  'cohort-extras',
  'enrolment-channel',
  'cohort-page',
  'calendar-practice-slots',
  'intake',
  'intake-desk',
  'referrals',
  'previsit-questionnaire',
  'care-plans',
  'pvs-visit-notes',
  'inbox-demo',
  'inbox-media',
  'inbox-templates',
  'clinic-values',
  'goals',
  'demog-box',
  'care-reports',
  'calc-autobind',
  'intake-type',
  'copay-warning',
  'growth-chart',
  'staff-chat',
  'staff-chat-patient',
  'step-role',
];
const STORAGE_KEY = 'scrips.practiceSwitches';

/**
 * Switches that are ON unless this browser turned them off. Their `?x=-name` is remembered in its own key
 * (STORAGE_OFF_KEY), so the default-off switches and their storage are unchanged.
 */
const DEFAULT_ON: ReadonlySet<PracticeSwitch> = new Set<PracticeSwitch>(['calendar-practice-slots', 'intake-desk', 'cohort-page', 'cohort-extras', 'care-reports', 'intake-type', 'inbox-media', 'inbox-templates']);
const STORAGE_OFF_KEY = 'scrips.practiceSwitchesOff';

/** Where DEV_DEFAULT_ON applies: the deployed dev site and a local build. */
const DEV_HOSTS: readonly string[] = ['dev-practice-v2.scrips.com', 'localhost', '127.0.0.1'];

/**
 * Default-off switches that read ON on DEV_HOSTS until this browser turns them off (Andrew, 3 Nov). Their off
 * choice shares STORAGE_OFF_KEY with DEFAULT_ON; elsewhere they read STORAGE_KEY as before.
 */
const DEV_DEFAULT_ON: ReadonlySet<PracticeSwitch> = new Set<PracticeSwitch>([
  'cohorts',
  'enrolment-channel',
  'intake',
  'referrals',
  'previsit-questionnaire',
  'care-plans',
  'pvs-visit-notes',
  'clinic-values',
  'goals',
  'demog-box',
  'calc-autobind',
  'copay-warning',
  'growth-chart',
  'step-role',
  'staff-chat',
]);

/** Module state, so `localStorage.clear()` cannot undo it. The unit setup pins it off (jsdom runs on localhost),
 *  so a suite written for the old screens keeps testing them; a test of the dev default turns it on. */
let devDefaultsOn = true;
export function pinDevDefaultsForTests(on: boolean): void {
  devDefaultsOn = on;
}

function devDefaultOn(name: PracticeSwitch): boolean {
  return devDefaultsOn && DEV_DEFAULT_ON.has(name) && typeof window !== 'undefined' && DEV_HOSTS.includes(window.location.hostname);
}

function readStored(key: string = STORAGE_KEY): Set<PracticeSwitch> {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is PracticeSwitch => KNOWN.includes(v as PracticeSwitch)));
  } catch {
    return new Set();
  }
}

function writeStored(switches: Set<PracticeSwitch>, key: string = STORAGE_KEY): void {
  try {
    window.localStorage.setItem(key, JSON.stringify([...switches]));
  } catch {
    // Storage unavailable: the switch simply stays as it was (OFF unless already stored).
  }
}

/** Applies any `x` query parameters to the stored switches. Unknown names are ignored. */
export function applyPracticeSwitchParams(search: string): void {
  const requested = new URLSearchParams(search).getAll('x');
  if (requested.length === 0) return;
  const switches = readStored();
  const turnedOff = readStored(STORAGE_OFF_KEY);
  let changed = false;
  let changedOff = false;
  for (const entry of requested) {
    const off = entry.startsWith('-');
    const name = (off ? entry.slice(1) : entry) as PracticeSwitch;
    if (!KNOWN.includes(name)) continue;
    if (DEFAULT_ON.has(name)) {
      // Default-on: remember only the "off" choice; `?x=name` clears it.
      if (off) turnedOff.add(name);
      else turnedOff.delete(name);
      changedOff = true;
      continue;
    }
    if (DEV_DEFAULT_ON.has(name)) {
      // Read from the off key on a dev host and the on key elsewhere, so keep both in step.
      if (off) turnedOff.add(name);
      else turnedOff.delete(name);
      changedOff = true;
    }
    if (off) switches.delete(name);
    else switches.add(name);
    changed = true;
  }
  if (changed) writeStored(switches);
  if (changedOff) writeStored(turnedOff, STORAGE_OFF_KEY);
}

/** True when this browser has turned the switch on (DEFAULT_ON switches, and DEV_DEFAULT_ON on a dev host: unless it
 *  turned them off). */
export function isPracticeSwitchOn(name: PracticeSwitch): boolean {
  if (DEFAULT_ON.has(name)) {
    if (typeof window === 'undefined') return true;
    applyPracticeSwitchParams(window.location.search);
    return !readStored(STORAGE_OFF_KEY).has(name);
  }
  if (typeof window === 'undefined') return false;
  applyPracticeSwitchParams(window.location.search);
  if (devDefaultOn(name)) return !readStored(STORAGE_OFF_KEY).has(name);
  return readStored().has(name);
}
